<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">
            <div class="return-mode-panel">
              <div class="mode-copy">
                <span class="mode-label">Return Type</span>
                <strong>{{ isManualMode ? "Manual return" : "Attach to bill" }}</strong>
              </div>
              <div class="mode-actions" role="group" aria-label="Sales return type">
                <button
                  type="button"
                  class="mode-option"
                  :class="{ active: !isManualMode }"
                  :disabled="isReadonly"
                  @click="setMode(false)"
                >
                  Attach to Bill
                </button>
                <button
                  type="button"
                  class="mode-option"
                  :class="{ active: isManualMode }"
                  :disabled="isReadonly"
                  @click="setMode(true)"
                >
                  Manual Return
                </button>
              </div>
            </div>

            <div class="header-grid">
              <div class="form-group">
                <label class="form-label">Return Date</label>
                <b-form-input v-model="header.returndate" type="date" class="form-ctrl" :disabled="isReadonly" />
              </div>
              <div class="form-group">
                <label class="form-label">Customer</label>
                <select
                  v-if="isManualMode"
                  v-model="header.customerid"
                  class="form-ctrl"
                  :disabled="isReadonly"
                  @change="applyManualCustomer"
                >
                  <option value="">Walk-in / No customer</option>
                  <option v-for="customer in customers" :key="customer.customerid" :value="customer.customerid">
                    {{ customer.customername }}{{ customer.mobile ? ` (${customer.mobile})` : "" }}
                  </option>
                </select>
                <b-form-input
                  v-else
                  :value="linkedCustomerLabel"
                  readonly
                  class="form-ctrl bg-light"
                />
              </div>
              <div class="form-group">
                <label class="form-label">Remarks</label>
                <b-form-textarea v-model="header.remarks" rows="2" class="form-ctrl textarea-ctrl" :disabled="isReadonly" />
              </div>
            </div>

            <div class="notes-grid compact-grid">
              <div class="form-group">
                <label class="form-label">Discount Amount</label>
                <b-form-input v-model.number="header.discountamount" type="number" class="form-ctrl" :disabled="isReadonly" />
              </div>
              <div class="form-group">
                <label class="form-label">Round Off</label>
                <b-form-input v-model.number="header.roundoff" type="number" class="form-ctrl" :disabled="isReadonly" />
              </div>
            </div>

            <template v-if="!isManualMode">
              <div class="section-head">
                <div>
                  <h4>Original Sale Lookup</h4>
                  <p>Find the bill, load it, then choose the items being returned.</p>
                </div>
              </div>

              <div class="lookup-bar">
                <input
                  v-model="saleSearch"
                  type="search"
                  class="form-ctrl lookup-input"
                  placeholder="Search sale bill, customer, or mobile..."
                  :disabled="isReadonly"
                  @input="searchSales"
                />
              </div>

              <div v-if="saleLoading" class="empty-panel">Searching sales...</div>
              <div v-else-if="saleSearch && availableSales.length" class="table-responsive mb-4">
                <table class="detail-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Bill Date</th>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th>Total Qty</th>
                      <th>Net Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="sale in availableSales" :key="sale.saleid">
                      <td>{{ sale.billno || sale.saleid }}</td>
                      <td>{{ formatDate(sale.billdate) }}</td>
                      <td>{{ sale.customername || "-" }}</td>
                      <td>{{ sale.mobile || "-" }}</td>
                      <td>{{ formatNumber(sale.totalqty) }}</td>
                      <td>Rs. {{ formatNumber(sale.netamount) }}</td>
                      <td class="text-end">
                        <button class="line-btn" :disabled="isReadonly" @click="selectSale(sale.saleid)">Load</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="selectedSale" class="selected-card">
                <div class="selected-meta">
                  <span>Sale Bill: <strong>{{ selectedSale.billno || selectedSale.saleid }}</strong></span>
                  <span>Customer: <strong>{{ selectedSale.customername || "-" }}</strong></span>
                  <span>Mobile: <strong>{{ selectedSale.mobile || "-" }}</strong></span>
                </div>

                <div v-if="saleDetailLoading" class="empty-panel">Loading sale allocations...</div>
                <div v-else-if="selectedSaleItems.length" class="table-responsive">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Batch</th>
                        <th>Sold Qty</th>
                        <th>Allocated Qty</th>
                        <th>Returned</th>
                        <th>Max Returnable</th>
                        <th>Rate</th>
                        <th>Tax Type</th>
                        <th>Tax %</th>
                        <th>Taxable</th>
                        <th>CGST</th>
                        <th>SGST</th>
                        <th>IGST</th>
                        <th>Expiry</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in selectedSaleItems" :key="row.key">
                        <td>{{ row.itemname }}</td>
                        <td>{{ row.batchno || "-" }}</td>
                        <td>{{ formatNumber(row.soldqty) }}</td>
                        <td>{{ formatNumber(row.allocatedqty) }}</td>
                        <td>{{ formatNumber(row.confirmedreturnedqty) }}</td>
                        <td>{{ formatNumber(row.maxreturnableqty) }}</td>
                        <td>Rs. {{ formatNumber(row.rate) }}</td>
                        <td>{{ row.taxtype || "GST" }}<span v-if="row.istaxinclusive" class="text-muted"> (Incl)</span></td>
                        <td>{{ formatNumber(row.taxpercent) }}</td>
                        <td>Rs. {{ formatNumber(row.taxablevalue) }}</td>
                        <td>Rs. {{ formatNumber(row.cgst) }}</td>
                        <td>Rs. {{ formatNumber(row.sgst) }}</td>
                        <td>Rs. {{ formatNumber(row.igst) }}</td>
                        <td>
                          {{ formatDate(row.expirydate) }}
                          <span v-if="row.expired" class="warn-chip">Expired</span>
                        </td>
                        <td class="text-end">
                          <button class="line-btn" :disabled="isReadonly || row.maxreturnableqty <= 0 || hasLinkedLine(row)" @click="addLinkedLine(row)">
                            {{ hasLinkedLine(row) ? "Added" : "Add" }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else-if="!saleDetailLoading" class="empty-panel">
                  No allocation rows available for this sale.
                </div>
              </div>
            </template>

            <template v-else>
              <div class="section-head">
                <div>
                  <h4>Manual Return Lines</h4>
                  <p>For returns that do not have an original sale bill.</p>
                </div>
                <button class="mini-btn" :disabled="isReadonly" @click="addManualLine">Add Manual Line</button>
              </div>
            </template>

            <div class="section-head">
              <div>
                <h4>Return Items</h4>
                <p>{{ isManualMode ? "Choose item, batch, quantity, and reason." : "Added bill items appear here before saving." }}</p>
              </div>
            </div>

            <div v-if="!lines.length" class="empty-panel">
              {{ isManualMode ? "Add a manual line to begin the return." : "Search and load a sale bill to add return items." }}
            </div>
            <div v-else class="table-responsive">
              <table class="detail-table">
                <thead>
                  <tr>
                    <th v-if="isManualMode">Item</th>
                    <th v-if="isManualMode">Batch</th>
                    <th v-if="!isManualMode">Original Sale</th>
                    <th>Item</th>
                    <th>Batch</th>
                    <th>Expiry</th>
                    <th>Return Qty</th>
                    <th>Max Returnable</th>
                    <th>Rate</th>
                    <th>Tax Type</th>
                    <th>Tax %</th>
                    <th>Taxable</th>
                    <th>CGST</th>
                    <th>SGST</th>
                    <th>IGST</th>
                    <th>Restockable</th>
                    <th>Reason</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, index) in lines" :key="line.key">
                    <template v-if="isManualMode">
                      <td>
                        <select v-model="line.itemid" class="table-input" :disabled="isReadonly" @change="onManualItemChange(line)">
                          <option value="">Select item</option>
                          <option v-for="item in itemOptions" :key="item.itemid" :value="item.itemid">{{ item.itemname }}</option>
                        </select>
                      </td>
                      <td>
                        <select v-model="line.purchaseitemid" class="table-input" :disabled="isReadonly || !line.itemid" @change="onManualBatchChange(line)">
                          <option value="">Select batch</option>
                          <option v-for="batch in line.batchOptions" :key="batch.purchaseitemid" :value="batch.purchaseitemid">
                            {{ batch.batchno || batch.billno || batch.purchaseid }} - {{ formatNumber(batch.remainingqty) }}
                          </option>
                        </select>
                      </td>
                    </template>
                    <template v-else>
                      <td>{{ (selectedSale && selectedSale.billno) || (selectedSale && selectedSale.saleid) || "-" }}</td>
                    </template>
                    <td>{{ line.itemname || "-" }}</td>
                    <td>{{ line.batchno || "-" }}</td>
                    <td>
                      {{ formatDate(line.expirydate) }}
                      <span v-if="isExpired(line.expirydate)" class="warn-chip">Expired</span>
                    </td>
                    <td>
                      <b-form-input v-model.number="line.returnqty" type="number" min="0" class="table-input" :disabled="isReadonly" @input="recalculateLine(line)" />
                    </td>
                    <td>{{ line.maxreturnableqty === null ? "-" : formatNumber(line.maxreturnableqty) }}</td>
                    <td>
                      <b-form-input v-model.number="line.rate" type="number" min="0" class="table-input" :disabled="isReadonly" @input="recalculateLine(line)" />
                    </td>
                    <td>{{ line.taxtype || "GST" }}<span v-if="line.istaxinclusive" class="text-muted"> (Incl)</span></td>
                    <td>
                      <b-form-input v-model.number="line.taxpercent" type="number" min="0" class="table-input" :disabled="isReadonly" @input="recalculateLine(line)" />
                    </td>
                    <td>Rs. {{ formatNumber(line.taxablevalue) }}</td>
                    <td>Rs. {{ formatNumber(line.cgst) }}</td>
                    <td>Rs. {{ formatNumber(line.sgst) }}</td>
                    <td>Rs. {{ formatNumber(line.igst) }}</td>
                    <td>
                      <label class="switch-wrap">
                        <input v-model="line.restockable" type="checkbox" :disabled="isReadonly" @change="handleRestockableToggle(line)" />
                        <span>{{ line.restockable ? "Yes" : "Damaged" }}</span>
                      </label>
                    </td>
                    <td>
                      <b-form-input v-model="line.reason" class="table-input" :disabled="isReadonly" />
                    </td>
                    <td>Rs. {{ formatNumber(lineTotal(line)) }}</td>
                    <td class="text-end">
                      <button class="remove-btn" :disabled="isReadonly" @click="removeLine(index)">Remove</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="expiredRestockableLines.length" class="warning-box">
              Some expired batches are marked restockable. The system allows this, but you should verify before confirming.
            </div>

            <div class="summary-card">
              <div class="summary-row">
                <span>Total Amount</span>
                <strong>Rs. {{ formatNumber(summary.totalamount) }}</strong>
              </div>
              <div class="summary-row">
                <span>Total Tax</span>
                <strong>Rs. {{ formatNumber(summary.taxamount) }}</strong>
              </div>
              <div class="summary-row">
                <span>Discount</span>
                <strong>Rs. {{ formatNumber(header.discountamount) }}</strong>
              </div>
              <div class="summary-row">
                <span>Round Off</span>
                <strong>Rs. {{ formatNumber(header.roundoff) }}</strong>
              </div>
              <div class="summary-row summary-row-net">
                <span>Net Amount</span>
                <strong>Rs. {{ formatNumber(summary.netamount) }}</strong>
              </div>
            </div>

            <div class="footer-actions">
              <button class="btn btn-primary" :disabled="saving || isReadonly" @click="saveDraft">
                {{ saving ? "Saving..." : returnId ? "Update Draft" : "Save Draft" }}
              </button>
              <button v-if="returnId && header.status === 'DRAFT'" class="btn btn-success" :disabled="saving" @click="confirmReturn">
                Confirm Return
              </button>
              <button v-if="returnId && ['DRAFT', 'CONFIRMED'].includes(header.status)" class="btn btn-danger" :disabled="saving" @click="cancelReturn">
                Cancel Return
              </button>
              <router-link to="/salesreturnlist" class="btn btn-secondary">Back</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const createManualLine = () => ({
  key: `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  salesitemid: null,
  purchaseitemid: "",
  itemid: "",
  itemname: "",
  batchno: "",
  expirydate: "",
  returnqty: 1,
  maxreturnableqty: null,
  rate: 0,
  costprice: 0,
  taxtype: "GST",
  taxpercent: 0,
  taxablevalue: 0,
  cgst: 0,
  sgst: 0,
  igst: 0,
  amount: 0,
  taxamount: 0,
  discount: 0,
  istaxinclusive: false,
  restockable: true,
  reason: "",
  batchOptions: [],
});

export default {
  name: "createsalesreturn",
  data() {
    return {
      manualMode: false,
      customers: [],
      itemOptions: [],
      availableSales: [],
      selectedSale: null,
      selectedSaleItems: [],
      saleSearch: "",
      saleLoading: false,
      saleDetailLoading: false,
      loading: false,
      saving: false,
      saleSearchTimer: null,
      lines: [],
      header: {
        billno: "",
        originalsaleid: null,
        customerid: "",
        customername: "",
        mobile: "",
        returndate: this.getToday(),
        locationid: this.$store.getters.getCurrentLocation?.locationid || "",
        status: "NEW",
        discountamount: 0,
        roundoff: 0,
        remarks: "",
      },
    };
  },
  computed: {
    returnId() {
      return this.$route.query.id || "";
    },
    title() {
      return this.returnId ? "Edit Sales Return" : "Create Sales Return";
    },
    title1() {
      return this.returnId ? "Review or update this sales return" : "Attach a return to an original bill";
    },
    isReadonly() {
      return Boolean(this.returnId) && this.header.status !== "DRAFT";
    },
    isManualMode() {
      return this.manualMode;
    },
    linkedCustomerLabel() {
      if (this.header.customername || this.header.mobile) {
        const mobile = this.header.mobile ? ` (${this.header.mobile})` : "";
        return `${this.header.customername || "Walk-in customer"}${mobile}`;
      }
      return "Customer will fill after selecting a sale bill";
    },
    statusClass() {
      return {
        "badge-draft": this.header.status === "DRAFT",
        "badge-confirmed": this.header.status === "CONFIRMED",
        "badge-cancelled": this.header.status === "CANCELLED",
      };
    },
    summary() {
      const totalamount = this.lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
      const taxamount = this.lines.reduce((sum, line) => sum + Number(line.taxamount || 0), 0);
      const discountamount = Number(this.header.discountamount || 0);
      const roundoff = Number(this.header.roundoff || 0);
      const baseNet = this.lines.reduce((sum, line) => sum + Number(this.lineTotal(line) || 0), 0);
      return {
        totalamount: Number(totalamount.toFixed(2)),
        taxamount: Number(taxamount.toFixed(2)),
        netamount: Number((baseNet - discountamount + roundoff).toFixed(2)),
      };
    },
    expiredRestockableLines() {
      return this.lines.filter((line) => line.restockable && this.isExpired(line.expirydate));
    },
    locationOptions() {
      return (this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      }));
    },
  },
  async beforeMount() {
    await Promise.all([this.fetchCustomers(), this.fetchItems()]);
    if (this.returnId) {
      await this.loadReturn();
    } else if (this.isManualMode) {
      this.addManualLine();
    }
  },
  beforeDestroy() {
    clearTimeout(this.saleSearchTimer);
  },
  watch: {
    "header.locationid": {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue || this.isReadonly) return;
        this.availableSales = [];
        this.selectedSale = null;
        this.selectedSaleItems = [];
        if (!this.isManualMode) {
          this.lines = [];
          if (this.saleSearch.trim()) {
            this.searchSales();
          }
          return;
        }
        await Promise.all(
          this.lines
            .filter((line) => line.itemid)
            .map((line) => this.onManualItemChange(line))
        );
      },
    },
  },
  methods: {
    getToday() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    },
    getCurrentUserId() {
      const attrs = this.$store?.getters?.getUserDetails;
      if (!Array.isArray(attrs)) return null;
      const sub = attrs.find((attr) => attr.Name === "sub");
      return sub?.Value || null;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    normalizeInclusiveFlag(value, fallback = false) {
      if (value === true || value === 1 || value === "1" || value === "true") return true;
      if (value === false || value === 0 || value === "0" || value === "false") return false;
      return fallback;
    },
    lineTotal(line) {
      const amount = Number(line.amount || 0);
      const tax = Number(line.taxamount || 0);
      return line.istaxinclusive ? amount : (amount + tax);
    },
    isExpired(value) {
      if (!value) return false;
      return new Date(value) < new Date(`${this.getToday()}T00:00:00`);
    },
    async fetchCustomers() {
      const response = await axios.get("/v1/customers");
      this.customers = response.data || [];
    },
    async fetchItems() {
      const response = await axios.get("/v1/sales/items");
      this.itemOptions = Array.isArray(response.data) ? response.data : response.data.items || [];
    },
    applyManualCustomer() {
      const customer = this.customers.find((row) => row.customerid === this.header.customerid);
      this.header.customername = customer?.customername || "";
      this.header.mobile = customer?.mobile || "";
    },
    setMode(isManual) {
      if (this.isReadonly) return;
      if (this.manualMode === isManual) return;
      this.manualMode = isManual;
      this.lines = [];
      if (!this.manualMode) {
        this.header.originalsaleid = null;
        this.header.customerid = "";
        this.header.customername = "";
        this.header.mobile = "";
        this.selectedSale = null;
        this.selectedSaleItems = [];
      } else {
        this.header.originalsaleid = null;
        this.header.customerid = "";
        this.header.customername = "";
        this.header.mobile = "";
        this.addManualLine();
      }
    },
    searchSales() {
      clearTimeout(this.saleSearchTimer);
      this.saleSearchTimer = setTimeout(async () => {
        if (!this.saleSearch.trim()) {
          this.availableSales = [];
          return;
        }
        this.saleLoading = true;
        try {
          const response = await axios.get("/v1/sales-returns/sale-search", {
            params: {
              search: this.saleSearch.trim(),
              locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
            },
          });
          this.availableSales = (response.data || []).map((row) => ({ ...row, key: row.saleid }));
        } catch (error) {
          Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to search sales." });
        } finally {
          this.saleLoading = false;
        }
      }, 300);
    },
    async selectSale(saleid) {
      this.saleDetailLoading = true;
      try {
        const response = await axios.get(`/v1/sales-returns/sales/${saleid}`, {
          params: {
            locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
          },
        });
        this.manualMode = false;
        this.selectedSale = response.data;
        this.selectedSaleItems = (response.data.items || []).map((row) => ({
          ...row,
          key: `${row.salesitemid}-${row.purchaseitemid || "none"}`,
        }));
        this.header.originalsaleid = response.data.saleid;
        this.header.customerid = response.data.customerid || "";
        this.header.customername = response.data.customername || "";
        this.header.mobile = response.data.mobile || "";
        this.lines = [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load sale detail." });
      } finally {
        this.saleDetailLoading = false;
      }
    },
    hasLinkedLine(row) {
      return this.lines.some(
        (line) => Number(line.salesitemid) === Number(row.salesitemid) && Number(line.purchaseitemid) === Number(row.purchaseitemid)
      );
    },
    addLinkedLine(row) {
      if (this.hasLinkedLine(row)) return;
      const line = {
        key: `linked-${row.salesitemid}-${row.purchaseitemid}`,
        salesitemid: row.salesitemid,
        purchaseitemid: row.purchaseitemid,
        itemid: row.itemid,
        itemname: row.itemname,
        batchno: row.batchno,
        expirydate: row.expirydate,
        returnqty: row.maxreturnableqty > 0 ? 1 : 0,
        maxreturnableqty: Number(row.maxreturnableqty || 0),
        rate: Number(row.rate || 0),
        costprice: Number(row.costprice || 0),
        taxtype: row.taxtype || "GST",
        taxpercent: Number(row.taxpercent || 0),
        taxablevalue: Number(row.taxablevalue || 0),
        cgst: Number(row.cgst || 0),
        sgst: Number(row.sgst || 0),
        igst: Number(row.igst || 0),
        amount: 0,
        taxamount: 0,
        discount: Number(row.discount || 0),
        istaxinclusive: this.normalizeInclusiveFlag(row.istaxinclusive, Number(row.rate || 0) > 0),
        restockable: true,
        reason: "",
        batchOptions: [],
      };
      this.recalculateLine(line);
      this.lines.push(line);
    },
    addManualLine() {
      this.lines.push(createManualLine());
    },
    async onManualItemChange(line) {
      const selectedItem = this.itemOptions.find((row) => row.itemid === line.itemid);
      line.itemname = selectedItem?.itemname || "";
      line.purchaseitemid = "";
      line.batchno = "";
      line.expirydate = "";
      line.batchOptions = [];
      if (!line.itemid) return;
      const response = await axios.get(`/v1/sales-returns/manual-batches/${line.itemid}`, {
        params: {
          locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
        },
      });
      line.batchOptions = response.data || [];
    },
    onManualBatchChange(line) {
      const batch = line.batchOptions.find((row) => Number(row.purchaseitemid) === Number(line.purchaseitemid));
      if (!batch) return;
      line.itemid = batch.itemid;
      line.itemname = batch.itemname;
      line.batchno = batch.batchno;
      line.expirydate = batch.expirydate;
      line.rate = Number(batch.mrp || 0);
      line.costprice = Number(batch.purchaseprice || 0);
      line.taxtype = batch.taxtype || "GST";
      line.taxpercent = Number(batch.taxpercent || 0);
      line.taxablevalue = Number(batch.taxablevalue || 0);
      line.cgst = Number(batch.cgst || 0);
      line.sgst = Number(batch.sgst || 0);
      line.igst = Number(batch.igst || 0);
      line.istaxinclusive = this.normalizeInclusiveFlag(batch.istaxinclusive, Number(batch.mrp || 0) > 0);
      this.recalculateLine(line);
    },
    recalculateLine(line) {
      if (line.maxreturnableqty !== null && Number(line.returnqty || 0) > Number(line.maxreturnableqty || 0)) {
        line.returnqty = Number(line.maxreturnableqty || 0);
      }
      const qty = Number(line.returnqty || 0);
      const rate = Number(line.rate || 0);
      const taxpercent = Number(line.taxpercent || 0);
      const gross = Number((qty * rate).toFixed(2));
      if (line.istaxinclusive && taxpercent > 0) {
        line.taxablevalue = Number((gross * 100 / (100 + taxpercent)).toFixed(2));
        line.amount = gross;
        line.taxamount = Number((gross - line.taxablevalue).toFixed(2));
      } else {
        line.taxablevalue = gross;
        line.amount = gross;
        line.taxamount = Number((line.taxablevalue * taxpercent / 100).toFixed(2));
      }
      line.cgst = Number((line.taxamount / 2).toFixed(2));
      line.sgst = Number((line.taxamount / 2).toFixed(2));
      line.igst = 0;
    },
    handleRestockableToggle(line) {
      if (line.restockable && this.isExpired(line.expirydate)) {
        Swal.fire({
          icon: "warning",
          title: "Expired batch warning",
          text: "This batch is expired. Are you sure it is restockable?",
        });
      }
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    buildPayload() {
      return {
        originalsaleid: this.isManualMode ? null : this.header.originalsaleid,
        locationid: this.header.locationid || null,
        customerid: this.header.customerid || null,
        customername: this.header.customername || null,
        mobile: this.header.mobile || null,
        returndate: this.header.returndate,
        discountamount: Number(this.header.discountamount || 0),
        taxamount: this.summary.taxamount,
        roundoff: Number(this.header.roundoff || 0),
        netamount: this.summary.netamount,
        remarks: this.header.remarks || null,
        createdby: this.getCurrentUserId(),
        items: this.lines.map((line) => ({
          salesitemid: line.salesitemid || null,
          purchaseitemid: Number(line.purchaseitemid),
          itemid: line.itemid,
          returnqty: Number(line.returnqty || 0),
          rate: Number(line.rate || 0),
          costprice: Number(line.costprice || 0),
          taxtype: line.taxtype || null,
          taxpercent: Number(line.taxpercent || 0),
          taxablevalue: Number(line.taxablevalue || 0),
          cgst: Number(line.cgst || 0),
          sgst: Number(line.sgst || 0),
          igst: Number(line.igst || 0),
          taxamount: Number(line.taxamount || 0),
          discount: Number(line.discount || 0),
          amount: Number(line.amount || 0),
          istaxinclusive: Boolean(line.istaxinclusive),
          batchno: line.batchno || null,
          expirydate: line.expirydate || null,
          restockable: Boolean(line.restockable),
          reason: line.reason || "",
        })),
      };
    },
    validateDraft() {
      if (!this.lines.length) {
        Swal.fire("Add at least one return line");
        return false;
      }
      if (!this.isManualMode && !this.header.originalsaleid) {
        Swal.fire("Select the original sale before saving");
        return false;
      }
      for (let index = 0; index < this.lines.length; index += 1) {
        const line = this.lines[index];
        if (!line.itemid) {
          Swal.fire(`Select an item for row ${index + 1}`);
          return false;
        }
        if (!line.purchaseitemid) {
          Swal.fire(`Select a batch for row ${index + 1}`);
          return false;
        }
        if (!(Number(line.returnqty) > 0)) {
          Swal.fire(`Return quantity must be greater than 0 for row ${index + 1}`);
          return false;
        }
        if (line.maxreturnableqty !== null && Number(line.returnqty) > Number(line.maxreturnableqty || 0)) {
          Swal.fire(`Return quantity exceeds the allowed batch quantity for row ${index + 1}`);
          return false;
        }
        if (!line.reason || !line.reason.trim()) {
          Swal.fire(`Reason is required for row ${index + 1}`);
          return false;
        }
      }
      return true;
    },
    async confirmExpiredRestockable() {
      if (!this.expiredRestockableLines.length) return true;
      const result = await Swal.fire({
        icon: "warning",
        title: "Expired restockable lines",
        text: "Some expired batches are marked restockable. Do you want to continue?",
        showCancelButton: true,
        confirmButtonText: "Continue",
      });
      return result.isConfirmed;
    },
    async saveDraft() {
      if (!this.validateDraft()) return;
      if (!(await this.confirmExpiredRestockable())) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.returnId) {
          await axios.put(`/v1/sales-returns/${this.returnId}`, payload);
          Swal.fire({ icon: "success", title: "Draft updated", timer: 1400, showConfirmButton: false });
        } else {
          const response = await axios.post("/v1/sales-returns", payload);
          const newId = response.data?.[0];
          Swal.fire({ icon: "success", title: "Draft saved", timer: 1400, showConfirmButton: false });
          if (newId) {
            await this.$router.replace({ path: "/createsalesreturn", query: { id: newId } });
            await this.loadReturn();
          }
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to save sales return draft." });
      } finally {
        this.saving = false;
      }
    },
    async loadReturn() {
      this.loading = true;
      try {
        const response = await axios.get(`/v1/sales-returns/${this.returnId}`);
        const detail = response.data;
        this.header = {
          billno: detail.billno,
          originalsaleid: detail.originalsaleid,
          customerid: detail.customerid || "",
          customername: detail.customername || "",
          mobile: detail.mobile || "",
          returndate: detail.returndate || this.getToday(),
          locationid: detail.locationid || this.$store.getters.getCurrentLocation?.locationid || "",
          status: detail.status,
          discountamount: Number(detail.discountamount || 0),
          roundoff: Number(detail.roundoff || 0),
          remarks: detail.remarks || "",
        };

        if (detail.originalsaleid) {
          this.manualMode = false;
          await this.selectSale(detail.originalsaleid);
        } else {
          this.manualMode = true;
        }

        const nextLines = [];
        for (const item of detail.items || []) {
          const line = {
            key: `loaded-${item.id}`,
            salesitemid: item.salesitemid,
            purchaseitemid: item.purchaseitemid,
            itemid: item.itemid,
            itemname: item.itemname,
            batchno: item.batchno,
            expirydate: item.expirydate,
            returnqty: Number(item.returnqty || 0),
            maxreturnableqty: null,
            rate: Number(item.rate || 0),
            costprice: Number(item.costprice || 0),
            taxtype: item.taxtype || "GST",
            taxpercent: Number(item.taxpercent || 0),
            taxablevalue: Number(item.taxablevalue || 0),
            cgst: Number(item.cgst || 0),
            sgst: Number(item.sgst || 0),
            igst: Number(item.igst || 0),
            amount: Number(item.amount || 0),
            taxamount: Number(item.taxamount || 0),
            discount: Number(item.discount || 0),
            istaxinclusive: this.normalizeInclusiveFlag(item.istaxinclusive, Number(item.rate || 0) > 0),
            restockable: Boolean(item.restockable),
            reason: item.reason || "",
            batchOptions: [],
          };
          if (item.salesitemid && this.selectedSaleItems.length) {
            const source = this.selectedSaleItems.find(
              (row) => Number(row.salesitemid) === Number(item.salesitemid) && Number(row.purchaseitemid) === Number(item.purchaseitemid)
            );
            line.maxreturnableqty = source ? Number(source.maxreturnableqty || 0) + Number(item.returnqty || 0) : Number(item.returnqty || 0);
          } else if (!item.salesitemid && item.itemid) {
            const batchResponse = await axios.get(`/v1/sales-returns/manual-batches/${item.itemid}`, {
              params: {
                locationid: detail.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
              },
            });
            line.batchOptions = batchResponse.data || [];
          }
          nextLines.push(line);
        }
        this.lines = nextLines.length ? nextLines : (this.isManualMode ? [createManualLine()] : []);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load sales return." });
      } finally {
        this.loading = false;
      }
    },
    async confirmReturn() {
      if (!(await this.confirmExpiredRestockable())) return;
      const result = await Swal.fire({
        title: "Confirm sales return?",
        text: "This will post stock and damaged-goods movements in one transaction.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, confirm",
      });
      if (!result.isConfirmed) return;
      this.saving = true;
      try {
        await axios.post(`/v1/sales-returns/${this.returnId}/confirm`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return confirmed", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to confirm sales return." });
      } finally {
        this.saving = false;
      }
    },
    async cancelReturn() {
      const result = await Swal.fire({
        title: "Cancel sales return?",
        text: this.header.status === "CONFIRMED"
          ? "This will write reversal ledger entries and may be blocked if returned stock was consumed again."
          : "This draft return will be cancelled.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;
      this.saving = true;
      try {
        await axios.post(`/v1/sales-returns/${this.returnId}/cancel`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return cancelled", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to cancel sales return." });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.header-grid,
.notes-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;
}
.return-mode-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  margin-bottom: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
}
.mode-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mode-label {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}
.mode-copy strong {
  color: #111827;
  font-size: 1rem;
}
.mode-actions {
  display: inline-flex;
  padding: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
}
.mode-option {
  min-height: 36px;
  border: 0;
  border-radius: 6px;
  padding: 8px 14px;
  background: transparent;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
}
.mode-option.active {
  background: #fff7ed;
  color: #c2410c;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}
.header-grid {
  grid-template-columns: minmax(180px, 220px) minmax(240px, 320px) minmax(0, 1fr);
  align-items: start;
}
.notes-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.compact-grid {
  grid-template-columns: repeat(2, minmax(0, 220px));
}
.form-label {
  display: block;
  margin-bottom: 6px;
  color: #6b7280;
  font-size: 0.82rem;
  font-weight: 700;
}
.form-ctrl {
  min-height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.textarea-ctrl {
  min-height: 88px;
}
.status-badge {
  min-height: 38px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 8px;
  font-weight: 700;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
}
.badge-draft {
  background: #fff7ed;
  border-color: #fdba74;
  color: #c2410c;
}
.badge-confirmed {
  background: #f0fdf4;
  border-color: #86efac;
  color: #15803d;
}
.badge-cancelled {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #dc2626;
}
.mini-btn,
.line-btn,
.remove-btn {
  min-height: 38px;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 700;
}
.mini-btn,
.line-btn {
  background: #fff7ed;
  color: #c2410c;
}
.remove-btn {
  background: #fef2f2;
  color: #dc2626;
}
.section-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin: 18px 0 10px;
}
.section-head h4 {
  margin: 0;
  font-size: 1rem;
  color: #111827;
}
.section-head p {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 0.8rem;
}
.lookup-bar {
  margin-bottom: 14px;
}
.lookup-input {
  width: min(100%, 420px);
}
.selected-card,
.empty-panel,
.warning-box {
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 16px;
}
.selected-card {
  border: 1px solid #fed7aa;
  background: #fffaf5;
}
.selected-meta {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  font-size: 0.82rem;
  color: #6b7280;
}
.empty-panel {
  border: 1px dashed #d1d5db;
  background: #f9fafb;
  color: #6b7280;
}
.warning-box {
  border: 1px solid #fdba74;
  background: #fff7ed;
  color: #9a3412;
}
.detail-table {
  width: 100%;
  border-collapse: collapse;
}
.detail-table th,
.detail-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.84rem;
  vertical-align: middle;
}
.detail-table th {
  background: #f8fafc;
  color: #64748b;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.table-input {
  min-width: 120px;
  min-height: 34px;
  border-radius: 8px;
}
.warn-chip {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 0.7rem;
  font-weight: 700;
}
.switch-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #374151;
}
.summary-card {
  margin-top: 18px;
  margin-left: auto;
  width: min(100%, 340px);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
}
.summary-row-net {
  background: #fff7ed;
  color: #c2410c;
}
.footer-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 20px;
}
@media (max-width: 1100px) {
  .header-grid,
  .notes-grid,
  .compact-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 768px) {
  .return-mode-panel {
    align-items: stretch;
    flex-direction: column;
  }
  .mode-actions {
    display: flex;
    width: 100%;
  }
  .mode-option {
    flex: 1;
  }
  .header-grid,
  .notes-grid,
  .compact-grid {
    grid-template-columns: 1fr;
  }
}
</style>
